/**
 * Self-hosted login screen.
 *
 * Mounted by main.jsx when AUTH_REQUIRED is on, no token is present,
 * and the `selfHostedLogin()` feature flag is on. The page is fully
 * styled in-app and POSTs credentials directly to gateway-side
 * wrappers around WorkOS User Management.
 *
 * Gateway endpoints this page calls (✅ = already live in origin's
 * grpc-gateway-api; ❌ = not yet implemented on the backend):
 *
 *   ✅ POST /v1/auth/token  (AuthenticateWithPassword)
 *     req:  { email, password, organization_id? }
 *     resp: { access_token, refresh_token, user }
 *
 *   ✅ POST /v1/auth/select-organization  (AuthenticateWithOrganization)
 *     req:  { pending_authentication_token, organization_id }
 *     resp: { access_token, refresh_token, user }
 *
 *   ❌ POST /v1/auth/magic/send
 *     req:  { email }
 *     resp: { ok: true }
 *     impl: WorkOS POST /user_management/magic_auth/send
 *
 *   ❌ POST /v1/auth/magic/verify
 *     req:  { email, code }
 *     resp: { access_token, refresh_token, user }
 *     impl: WorkOS POST /user_management/authenticate
 *           grant_type=urn:workos:oauth:grant-type:magic-auth:code
 *
 *   ❌ POST /v1/auth/password-reset/send
 *     req:  { email }
 *     resp: { ok: true }
 *     impl: WorkOS POST /user_management/password_reset/send
 *
 *   ❌ POST /v1/auth/password-reset/confirm
 *     req:  { token, new_password }
 *     resp: { access_token, refresh_token, user }
 *     impl: WorkOS POST /user_management/password_reset/confirm
 *           (returns tokens for the now-updated user)
 *
 * Password-reset email link should redirect users to
 * `https://<dashboard-host>/?password_reset_token=…` — authBootstrap
 * detects that param on load and routes here in reset-password mode.
 *
 * Response shape mirrors /v1/auth/exchange so persistTokens() works
 * unchanged. On success we reload — the next authBootstrap pass sees
 * the token in localStorage and mounts the app.
 *
 * SSO ("Continue with Google" / "Continue with SSO") falls back to
 * the existing WorkOS redirect flow via redirectToWorkos(), so it
 * works without any new gateway routes.
 */

import { useEffect, useState } from "react";
import {
    AUTH_API_BASE,
    authExchangeFailed,
    getAuthExchangeRetryDelay,
    scheduleAuthExchangeRecovery,
    buildAuthorizeUrl,
    clearAuthBootstrapState,
    clearPasswordResetToken,
    clearPendingOrgSelection,
    getPasswordResetToken,
    getPendingOrgSelection,
    persistTokens,
    redirectToWorkos,
} from "../lib/authBootstrap.js";
import OriginMark from "../components/OriginMark.jsx";

// Self-hosted SSO buttons. Each one redirects to WorkOS's
// /user_management/authorize with a specific `provider` — WorkOS
// 302s straight to the IdP without rendering its own UI. To add a
// customer-specific SAML/OIDC connection, append:
//   { label: "Sign in with Okta (Acme)", connectionId: "conn_xxx" }
const SSO_PROVIDERS = [
    { label: "Continue with Google", provider: "GoogleOAuth" },
    { label: "Continue with Microsoft", provider: "MicrosoftOAuth" },
];

const inputCls =
    "min-h-[34px] w-full rounded-[2px] border border-[color:var(--line)] bg-[color:var(--bg)] px-[10px] text-[13px] leading-[1.2] text-[color:var(--ink)] outline-none placeholder:text-[color:var(--muted-soft)] focus:border-[color:var(--ink)] transition-colors";

const primaryBtnCls =
    "h-[34px] w-full rounded-[2px] border border-[color:var(--green)] bg-[color:var(--green-soft)] px-[12px] text-[11px] leading-[1.08] tracking-[0.04em] uppercase text-[color:var(--ink)] font-medium hover:border-[color:var(--green)] disabled:opacity-50 cursor-pointer transition-colors";

const secondaryBtnCls =
    "h-[34px] w-full rounded-[2px] border border-[color:var(--line)] bg-transparent px-[12px] text-[11px] leading-[1.08] tracking-[0.04em] uppercase text-[color:var(--muted)] hover:text-[color:var(--ink)] hover:border-[color:var(--ink)] disabled:opacity-50 cursor-pointer transition-colors";

const linkBtnCls =
    "text-[11px] leading-[1.2] text-[color:var(--muted)] hover:text-[color:var(--ink)] underline underline-offset-2 cursor-pointer bg-transparent border-0 p-0";

const EYEBROW_CLASS =
    "text-[9px] leading-[1] tracking-[0.18em] uppercase font-medium";

async function postJson(path, body) {
    const res = await fetch(`${AUTH_API_BASE}${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
    });
    let payload = null;
    try {
        payload = await res.json();
    } catch {}
    if (!res.ok) {
        const msg =
            payload?.error?.message ||
            payload?.message ||
            `${res.status} ${res.statusText}`;
        throw new Error(msg);
    }
    return payload || {};
}

function FormField({ label, hint, error, children }) {
    return (
        <label className="grid gap-[5px]">
            <span
                className={`${EYEBROW_CLASS} text-[color:var(--muted)]`}
            >
                {label}
            </span>
            {children}
            {error ? (
                <span className="text-[11px] leading-[1.3] text-red-400">
                    {error}
                </span>
            ) : hint ? (
                <span className="text-[11px] leading-[1.3] text-[color:var(--muted)]">
                    {hint}
                </span>
            ) : null}
        </label>
    );
}

export default function LoginPage() {
    // Multi-org users hit `organization_selection_required` during the
    // code exchange — authBootstrap stashes the pending state in
    // sessionStorage. If it's there, we render an org picker instead
    // of the credentials form. Picking an org rebuilds the WorkOS
    // authorize URL with `organization_id` pinned and redirects;
    // WorkOS bounces back with a fresh code already bound to that org.
    const [pendingOrg, setPendingOrg] = useState(() => getPendingOrgSelection());
    // O-1321: we land here (rather than auto-redirecting) when a WorkOS
    // code exchange just failed. Surface that once so the user knows the
    // last attempt didn't go through, instead of a bare sign-in form.
    const [exchangeFailed] = useState(() => authExchangeFailed());
    const [willRetryExchange] = useState(() => getAuthExchangeRetryDelay() !== null);
    useEffect(() => {
        if (exchangeFailed) return scheduleAuthExchangeRecovery();
    }, [exchangeFailed]);
    // mode: "password" | "magic-email" | "magic-code"
    //     | "forgot-email" | "forgot-sent" | "reset-password"
    // Default to "reset-password" if we arrived here from a password-
    // reset email link (token already stashed by authBootstrap).
    const [mode, setMode] = useState(() =>
        getPasswordResetToken() ? "reset-password" : "password",
    );
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [passwordConfirm, setPasswordConfirm] = useState("");
    const [code, setCode] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    async function pickOrg(orgId) {
        if (!pendingOrg?.pendingToken) {
            setError("Missing pending authentication token; please sign in again.");
            return;
        }
        setError("");
        setBusy(true);
        try {
            const data = await postJson("/v1/auth/select-organization", {
                pending_authentication_token: pendingOrg.pendingToken,
                organization_id: orgId,
            });
            clearPendingOrgSelection();
            handleSuccess(data);
        } catch (err) {
            setError(err.message || "Couldn't switch to that organization.");
            setBusy(false);
        }
    }

    function cancelOrgPicker() {
        clearPendingOrgSelection();
        setPendingOrg(null);
    }

    function handleSuccess(data) {
        if (!persistTokens(data)) {
            setError("Sign-in succeeded but no access token was returned.");
            setBusy(false);
            return;
        }
        // Reload — authBootstrap reads the fresh token and mounts <App />.
        window.location.reload();
    }

    async function submitPassword(e) {
        e.preventDefault();
        setError("");
        setBusy(true);
        try {
            const data = await postJson("/v1/auth/token", {
                email: email.trim(),
                password,
            });
            handleSuccess(data);
        } catch (err) {
            setError(err.message || "Sign-in failed.");
            setBusy(false);
        }
    }

    async function submitMagicSend(e) {
        e.preventDefault();
        setError("");
        setBusy(true);
        try {
            await postJson("/v1/auth/magic/send", { email: email.trim() });
            setMode("magic-code");
        } catch (err) {
            setError(err.message || "Couldn't send code.");
        } finally {
            setBusy(false);
        }
    }

    async function submitMagicVerify(e) {
        e.preventDefault();
        setError("");
        setBusy(true);
        try {
            const data = await postJson("/v1/auth/magic/verify", {
                email: email.trim(),
                code: code.trim(),
            });
            handleSuccess(data);
        } catch (err) {
            setError(err.message || "Invalid code.");
            setBusy(false);
        }
    }

    async function submitForgotSend(e) {
        e.preventDefault();
        setError("");
        setBusy(true);
        try {
            await postJson("/v1/auth/password-reset/send", {
                email: email.trim(),
            });
            setMode("forgot-sent");
        } catch (err) {
            setError(err.message || "Couldn't send reset link.");
        } finally {
            setBusy(false);
        }
    }

    async function submitResetPassword(e) {
        e.preventDefault();
        setError("");
        if (password.length < 8) {
            setError("Password must be at least 8 characters.");
            return;
        }
        if (password !== passwordConfirm) {
            setError("Passwords don't match.");
            return;
        }
        const token = getPasswordResetToken();
        if (!token) {
            setError("Reset link is missing or expired. Request a new one.");
            return;
        }
        setBusy(true);
        try {
            const data = await postJson("/v1/auth/password-reset/confirm", {
                token,
                new_password: password,
            });
            clearPasswordResetToken();
            handleSuccess(data);
        } catch (err) {
            setError(err.message || "Couldn't reset password.");
            setBusy(false);
        }
    }

    function handleSSO(opts) {
        setError("");
        setBusy(true);
        redirectToWorkos(opts);
    }

    // An explicit retry resets the redirect budget as well as stale flow state.
    function handleStartOver() {
        clearAuthBootstrapState();
        window.location.assign("/");
    }

    const showPassword = mode === "password";
    const showMagicEmail = mode === "magic-email";
    const showMagicCode = mode === "magic-code";
    const showForgotEmail = mode === "forgot-email";
    const showForgotSent = mode === "forgot-sent";
    const showResetPassword = mode === "reset-password";
    // SSO buttons and the "switch to magic/password" toggle only make
    // sense on the primary credentials screens, not in the forgot or
    // reset flows.
    const showSSO = showPassword || showMagicEmail;
    const showModeToggle = showPassword || showMagicEmail;

    function startForgot() {
        setMode("forgot-email");
        setError("");
        setPassword("");
        setCode("");
    }

    function backToSignIn() {
        setMode("password");
        setError("");
        setPassword("");
        setPasswordConfirm("");
        setCode("");
    }

    return (
        <div data-shell data-sidebar-collapsed="false">
            {/* Same origin rail as the app shell. The data-bound ScopeSidebar
                can't render without auth, so the drawer (data-sidebar) is
                left empty. The form sits inside [data-main] so it centers
                in the area to the right of the rail + sidebar (matching
                where the app's content would sit). data-layout="endpoint"
                collapses data-main's default 2-row grid into a single full-
                height row so place-items-center actually centers vertically. */}
            <aside data-origin-rail>
                <div data-origin-rail-head />
                <span />
                <div className="self-end w-[16px] overflow-visible py-[5px]">
                    <OriginMark className="w-full" />
                </div>
            </aside>
            <div data-main data-layout="endpoint" style={{ left: 'calc(var(--origin-rail))' }}>
                <div className="grid h-full w-full place-items-center px-[20px] py-[40px] text-[color:var(--ink)]">
                    <div className="grid w-full max-w-[360px] gap-[28px]">
                {pendingOrg ? (
                    <>
                        <header className="grid gap-[8px] text-center">
                            <h1 className="m-0 text-[20px] leading-[1.15] font-medium tracking-[-0.03em] text-[color:var(--ink)]">
                                Choose an organization
                            </h1>
                            {pendingOrg.user?.email ? (
                                <p className="m-0 text-[12px] leading-[1.45] text-[color:var(--muted)]">
                                    Signed in as{" "}
                                    <span className="text-[color:var(--ink)]">
                                        {pendingOrg.user.email}
                                    </span>
                                    .
                                </p>
                            ) : null}
                        </header>
                        <div className="grid gap-[8px] border border-[color:var(--line)] bg-[color:var(--bg)] p-[12px]">
                            {pendingOrg.organizations.length === 0 ? (
                                <div className="px-[8px] py-[10px] text-[12px] leading-[1.4] text-[color:var(--muted)]">
                                    No organizations available for this user.
                                </div>
                            ) : (
                                pendingOrg.organizations.map((org) => (
                                    <button
                                        key={org.id}
                                        type="button"
                                        onClick={() => pickOrg(org.id)}
                                        disabled={busy}
                                        className="flex items-center justify-between gap-[10px] rounded-[2px] border border-[color:var(--line)] bg-transparent px-[12px] py-[10px] text-left text-[13px] leading-[1.2] text-[color:var(--ink)] hover:border-[color:var(--ink)] disabled:opacity-50 cursor-pointer transition-colors"
                                    >
                                        <span className="overflow-hidden text-ellipsis whitespace-nowrap font-medium">
                                            {org.name}
                                        </span>
                                        <span className="shrink-0 text-[10px] tracking-[0.18em] uppercase text-[color:var(--muted)]">
                                            {busy ? "…" : "→"}
                                        </span>
                                    </button>
                                ))
                            )}
                            {error ? (
                                <div className="text-[11px] leading-[1.3] text-red-400">
                                    {error}
                                </div>
                            ) : null}
                        </div>
                        <div className="flex items-center justify-center gap-[14px]">
                            <button
                                type="button"
                                className={linkBtnCls}
                                onClick={cancelOrgPicker}
                                disabled={busy}
                            >
                                Sign in as a different user
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                {exchangeFailed ? (
                    <div className="grid gap-[8px] rounded-[2px] border border-red-500/40 bg-red-500/10 px-[12px] py-[10px]">
                        <span className="text-[12px] leading-[1.4] text-red-300">
                            {willRetryExchange
                                ? "Sign-in was interrupted. Retrying shortly. You can also try again now."
                                : "We couldn't complete your last sign-in. Please try again."}
                        </span>
                        <button
                            type="button"
                            className="justify-self-start text-[11px] leading-[1.2] text-red-300 hover:text-red-200 underline underline-offset-2 cursor-pointer bg-transparent border-0 p-0"
                            onClick={handleStartOver}
                        >
                            Try sign-in again
                        </button>
                    </div>
                ) : null}
                <header className="grid gap-[8px] text-center">
                    <h1 className="m-0 text-[20px] leading-[1.15] font-medium tracking-[-0.03em] text-[color:var(--ink)]">
                        {showMagicCode
                            ? "Check your email"
                            : showForgotEmail
                              ? "Reset your password"
                              : showForgotSent
                                ? "Check your email"
                                : showResetPassword
                                  ? "Set a new password"
                                  : "Sign in"}
                    </h1>
                    {showMagicCode ? (
                        <p className="m-0 text-[12px] leading-[1.45] text-[color:var(--muted)]">
                            We sent a code to{" "}
                            <span className="text-[color:var(--ink)]">
                                {email}
                            </span>
                            .
                        </p>
                    ) : showForgotEmail ? (
                        <p className="m-0 text-[12px] leading-[1.45] text-[color:var(--muted)]">
                            Enter your work email and we'll send you a link
                            to set a new password.
                        </p>
                    ) : showForgotSent ? (
                        <p className="m-0 text-[12px] leading-[1.45] text-[color:var(--muted)]">
                            If an account exists for{" "}
                            <span className="text-[color:var(--ink)]">
                                {email}
                            </span>
                            , you'll get a reset link shortly.
                        </p>
                    ) : null}
                </header>

                <div className="grid gap-[14px] border border-[color:var(--line)] bg-[color:var(--bg)] p-[20px]">
                    {showPassword ? (
                        <form
                            onSubmit={submitPassword}
                            className="grid gap-[14px]"
                            noValidate
                        >
                            <FormField label="Work email">
                                <input
                                    type="email"
                                    autoComplete="username"
                                    autoFocus
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="you@company.com"
                                    className={inputCls}
                                    disabled={busy}
                                />
                            </FormField>
                            <FormField label="Password">
                                <input
                                    type="password"
                                    autoComplete="current-password"
                                    required
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="••••••••"
                                    className={inputCls}
                                    disabled={busy}
                                />
                            </FormField>
                            {error ? (
                                <div className="text-[11px] leading-[1.3] text-red-400">
                                    {error}
                                </div>
                            ) : null}
                            <button
                                type="submit"
                                className={primaryBtnCls}
                                disabled={busy || !email || !password}
                            >
                                {busy ? "Signing in…" : "Sign in"}
                            </button>
                            <button
                                type="button"
                                className={`${linkBtnCls} justify-self-center`}
                                onClick={startForgot}
                                disabled={busy}
                            >
                                Forgot password?
                            </button>
                        </form>
                    ) : null}

                    {showMagicEmail ? (
                        <form
                            onSubmit={submitMagicSend}
                            className="grid gap-[14px]"
                            noValidate
                        >
                            <FormField
                                label="Work email"
                                hint="We'll email you a one-time code."
                            >
                                <input
                                    type="email"
                                    autoComplete="username"
                                    autoFocus
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="you@company.com"
                                    className={inputCls}
                                    disabled={busy}
                                />
                            </FormField>
                            {error ? (
                                <div className="text-[11px] leading-[1.3] text-red-400">
                                    {error}
                                </div>
                            ) : null}
                            <button
                                type="submit"
                                className={primaryBtnCls}
                                disabled={busy || !email}
                            >
                                {busy ? "Sending code…" : "Email me a code"}
                            </button>
                        </form>
                    ) : null}

                    {showMagicCode ? (
                        <form
                            onSubmit={submitMagicVerify}
                            className="grid gap-[14px]"
                            noValidate
                        >
                            <FormField label="One-time code">
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    autoComplete="one-time-code"
                                    autoFocus
                                    required
                                    value={code}
                                    onChange={(e) => setCode(e.target.value)}
                                    placeholder="123456"
                                    className={inputCls}
                                    disabled={busy}
                                />
                            </FormField>
                            {error ? (
                                <div className="text-[11px] leading-[1.3] text-red-400">
                                    {error}
                                </div>
                            ) : null}
                            <button
                                type="submit"
                                className={primaryBtnCls}
                                disabled={busy || !code}
                            >
                                {busy ? "Verifying…" : "Verify & sign in"}
                            </button>
                            <button
                                type="button"
                                className={linkBtnCls}
                                onClick={() => {
                                    setMode("magic-email");
                                    setCode("");
                                    setError("");
                                }}
                            >
                                Use a different email
                            </button>
                        </form>
                    ) : null}

                    {showForgotEmail ? (
                        <form
                            onSubmit={submitForgotSend}
                            className="grid gap-[14px]"
                            noValidate
                        >
                            <FormField label="Work email">
                                <input
                                    type="email"
                                    autoComplete="username"
                                    autoFocus
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="you@company.com"
                                    className={inputCls}
                                    disabled={busy}
                                />
                            </FormField>
                            {error ? (
                                <div className="text-[11px] leading-[1.3] text-red-400">
                                    {error}
                                </div>
                            ) : null}
                            <button
                                type="submit"
                                className={primaryBtnCls}
                                disabled={busy || !email}
                            >
                                {busy ? "Sending link…" : "Send reset link"}
                            </button>
                        </form>
                    ) : null}

                    {showForgotSent ? (
                        <div className="grid gap-[12px]">
                            <p className="m-0 text-[12px] leading-[1.45] text-[color:var(--muted)]">
                                Click the link in the email to set a new
                                password. The link expires in 1 hour.
                            </p>
                        </div>
                    ) : null}

                    {showResetPassword ? (
                        <form
                            onSubmit={submitResetPassword}
                            className="grid gap-[14px]"
                            noValidate
                        >
                            <FormField
                                label="New password"
                                hint="At least 8 characters."
                            >
                                <input
                                    type="password"
                                    autoComplete="new-password"
                                    autoFocus
                                    required
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="••••••••"
                                    className={inputCls}
                                    disabled={busy}
                                />
                            </FormField>
                            <FormField label="Confirm new password">
                                <input
                                    type="password"
                                    autoComplete="new-password"
                                    required
                                    value={passwordConfirm}
                                    onChange={(e) =>
                                        setPasswordConfirm(e.target.value)
                                    }
                                    placeholder="••••••••"
                                    className={inputCls}
                                    disabled={busy}
                                />
                            </FormField>
                            {error ? (
                                <div className="text-[11px] leading-[1.3] text-red-400">
                                    {error}
                                </div>
                            ) : null}
                            <button
                                type="submit"
                                className={primaryBtnCls}
                                disabled={
                                    busy || !password || !passwordConfirm
                                }
                            >
                                {busy ? "Saving…" : "Set new password"}
                            </button>
                        </form>
                    ) : null}

                    {showSSO ? (
                        <>
                            <div className="flex items-center gap-[10px] text-[10px] tracking-[0.18em] uppercase text-[color:var(--muted)]">
                                <span className="h-px flex-1 bg-[color:var(--line)]" />
                                or
                                <span className="h-px flex-1 bg-[color:var(--line)]" />
                            </div>
                            <div className="grid gap-[8px]">
                                {SSO_PROVIDERS.map((p) => (
                                    <button
                                        key={p.label}
                                        type="button"
                                        onClick={() =>
                                            handleSSO({
                                                provider: p.provider,
                                                connectionId: p.connectionId,
                                            })
                                        }
                                        className={secondaryBtnCls}
                                        disabled={busy}
                                    >
                                        {p.label}
                                    </button>
                                ))}
                            </div>
                        </>
                    ) : null}
                </div>

                {showModeToggle ? (
                    <div className="flex items-center justify-center gap-[14px]">
                        <button
                            type="button"
                            className={linkBtnCls}
                            onClick={() => {
                                setMode(showPassword ? "magic-email" : "password");
                                setError("");
                                setPassword("");
                                setCode("");
                            }}
                        >
                            {showPassword
                                ? "Sign in with email code instead"
                                : "Sign in with password instead"}
                        </button>
                    </div>
                ) : null}

                {(showForgotEmail || showForgotSent || showResetPassword) ? (
                    <div className="flex items-center justify-center gap-[14px]">
                        <button
                            type="button"
                            className={linkBtnCls}
                            onClick={backToSignIn}
                            disabled={busy}
                        >
                            Back to sign in
                        </button>
                    </div>
                ) : null}
                    </>
                )}
                    </div>
                </div>
            </div>
        </div>
    );
}
